/**
 * ClientOverviewTab — KPIs, contract summary, and recent messages
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { TrendingUp, Phone, Package, Calendar, MessageSquare, Heart, CheckSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import type { ClientDetail, ClientContract, ClientLead } from "@/hooks/useAMClientDetail";
import ClientHealthBadge from "@/components/am/ClientHealthBadge";
import HealthScoreBreakdown from "@/components/am/HealthScoreBreakdown";
import HealthTrendChart from "@/components/am/HealthTrendChart";

interface OpenTask {
  id: string;
  title: string;
  priority: string;
  dueDate: string | null;
  status: string;
}

interface Props {
  agent: ClientDetail;
  contracts: ClientContract[];
  leads: ClientLead[];
  recentMessages: { body: string; sender_role: string; created_at: string }[];
  openTasks?: OpenTask[];
  healthScore?: {
    overall: number;
    status: 'healthy' | 'neutral' | 'at_risk' | 'critical';
    delivery: number;
    engagement: number;
    billing: number;
    contract: number;
    riskFactors: string[];
    trend7d: number | null;
    trend30d: number | null;
    history: { date: string; score: number }[];
    loading: boolean;
  };
}

export default function ClientOverviewTab({ agent, contracts, leads, recentMessages, openTasks, healthScore }: Props) {
  const contactRate = agent.performance_metrics
    ? ((agent.performance_metrics as any).contact_rate ?? null)
    : null;

  const activeContracts = contracts.filter((c) => c.status === "active");

  // Count leads from actual data (more reliable than the counter field)
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const leadsThisMonth = leads.filter((l) => l.created_at >= monthStart).length;
  const leadsToday = leads.filter((l) => l.created_at >= todayStart).length;
  const totalLeads = leads.length;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className={`grid grid-cols-2 gap-4 ${healthScore && !healthScore.loading ? "md:grid-cols-5" : "md:grid-cols-4"}`}>
        {healthScore && !healthScore.loading && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">Health Score</CardTitle>
              <Heart className="h-3.5 w-3.5 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <ClientHealthBadge
                score={healthScore.overall}
                status={healthScore.status}
                delta7d={healthScore.trend7d}
              />
            </CardContent>
          </Card>
        )}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground">Leads This Month</CardTitle>
            <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{leadsThisMonth}</div>
            <p className="text-[10px] text-muted-foreground mt-1">
              {leadsToday} today &middot; {totalLeads} total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground">Contact Rate</CardTitle>
            <Phone className="h-3.5 w-3.5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {contactRate !== null ? `${Math.round(contactRate)}%` : "—"}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Score: {agent.performance_score}/100
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground">Delivery Progress</CardTitle>
            <Package className="h-3.5 w-3.5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {(() => {
              const totalDelivered = activeContracts.reduce((s, c) => s + c.leads_delivered, 0);
              const totalCommitted = activeContracts.reduce((s, c) => s + c.total_leads_committed, 0);
              const pct = totalCommitted > 0 ? Math.round((totalDelivered / totalCommitted) * 100) : 0;
              return (
                <>
                  <div className="text-2xl font-bold">{totalDelivered} / {totalCommitted}</div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {pct}% fulfilled
                  </p>
                </>
              );
            })()}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground">Active Since</CardTitle>
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {agent.assigned_at
                ? format(new Date(agent.assigned_at), "MMM yyyy")
                : "—"}
            </div>
            {agent.assigned_at && (
              <p className="text-[10px] text-muted-foreground mt-1">
                {format(new Date(agent.assigned_at), "MMM d, yyyy")}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Health Score Breakdown & Trend */}
      {healthScore && !healthScore.loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <HealthScoreBreakdown
            delivery={healthScore.delivery}
            engagement={healthScore.engagement}
            billing={healthScore.billing}
            contract={healthScore.contract}
            riskFactors={healthScore.riskFactors}
          />
          <HealthTrendChart history={healthScore.history} />
        </div>
      )}

      {/* Open Tasks for this client */}
      {openTasks && openTasks.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <CheckSquare className="h-4 w-4" />
              Open Tasks ({openTasks.length})
            </CardTitle>
            <Link
              to={`/am/tasks?client=${agent.id}`}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              View all
            </Link>
          </CardHeader>
          <CardContent className="space-y-2">
            {openTasks.slice(0, 5).map((task) => {
              const prioColors: Record<string, string> = {
                urgent: "bg-red-100 text-red-700 border-red-200",
                high: "bg-orange-100 text-orange-700 border-orange-200",
                medium: "bg-blue-100 text-blue-700 border-blue-200",
                low: "bg-slate-100 text-slate-600 border-slate-200",
              };
              const isOverdue = task.dueDate && new Date(task.dueDate) < new Date();
              return (
                <div key={task.id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant="outline" className={`text-[10px] px-1.5 shrink-0 ${prioColors[task.priority] || ""}`}>
                      {task.priority}
                    </Badge>
                    <span className="truncate">{task.title}</span>
                  </div>
                  {task.dueDate && (
                    <span className={`text-xs shrink-0 ml-2 ${isOverdue ? "text-red-600 font-medium" : "text-muted-foreground"}`}>
                      {new Date(task.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Active Contracts Summary */}
      {activeContracts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Active Contracts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeContracts.map((c) => {
              const pct = c.total_leads_committed > 0
                ? Math.round((c.leads_delivered / c.total_leads_committed) * 100)
                : 0;
              return (
                <div key={c.id} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-medium capitalize">{c.contract_type.replace(/_/g, " ")}</span>
                      <Badge variant="outline" className="text-[10px]">{c.priority_tier}</Badge>
                      {c.paused_at && (
                        <Badge variant="secondary" className="text-[10px]">Paused</Badge>
                      )}
                      {c.cancel_at_period_end && (
                        <Badge variant="destructive" className="text-[10px]">Cancelling</Badge>
                      )}
                    </div>
                    <span className="text-muted-foreground text-xs">
                      {c.leads_delivered} / {c.total_leads_committed} leads
                    </span>
                  </div>
                  <Progress value={pct} className="h-2" />
                  {c.lead_types && c.lead_types.length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                      {c.lead_types.map((lt) => (
                        <Badge key={lt} variant="outline" className="text-[10px] font-normal">
                          {lt}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Recent Messages */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Recent Messages
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentMessages.length === 0 ? (
            <p className="text-sm text-muted-foreground">No messages yet.</p>
          ) : (
            <div className="space-y-3">
              {recentMessages.map((msg, i) => (
                <div key={i} className="flex gap-3 text-sm">
                  <Badge variant={msg.sender_role === "am" ? "default" : "secondary"} className="text-[10px] flex-shrink-0 h-5">
                    {msg.sender_role === "am" ? "You" : "Client"}
                  </Badge>
                  <p className="text-muted-foreground truncate flex-1">{msg.body}</p>
                  <span className="text-[10px] text-muted-foreground flex-shrink-0">
                    {format(new Date(msg.created_at), "MMM d")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
